import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {
  EventPromotionReducerState,
  PrintFlyerPoster,
  PromotionStep,
  PromotionSteps,
  PromotionType,
} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types';
import {getDummyEventItem} from '@Libraries/events.library';
import {LoadingStates} from '@Utils/loading.util';
import {mystuffEventPromotionPageExtraReducers} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page-extra-reducers';
import type {EmailCampaign, EmailCampaignId} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {insert} from '@Utils/array.util';
import type {SocialPost} from '@Components/social-media/post.vo';

const initialState: EventPromotionReducerState = {
  event: getDummyEventItem(),
  loadingState: LoadingStates.NOT_LOADED,
  stats: {
    views: 0,
    registrations: 0,
    reach: 0,
  },
  promotionSteps: [],
  socialPosts: [],
  emailCampaigns: [],
  printFlyers: [],
  mobileViewActivePromotionType: undefined,
  showOneClickPromotionBanner: false,
  areEventPreviewsReady: false
};
const eventPromotionPageSlice = createSlice({
  name: 'eventPromotionPageSlice',
  initialState,
  reducers: {
    updatePromotionSteps: (state, action: PayloadAction<PromotionSteps>): void => {
      state.promotionSteps = action.payload;
    },

    updatePromotionStep: (state, action: PayloadAction<PromotionStep>): void => {
      const promotionStepIndex = state.promotionSteps.findIndex((promotionStep) => {
        return promotionStep.type === action.payload.type;
      });

      if (promotionStepIndex >= 0) {
        state.promotionSteps[promotionStepIndex] = action.payload;
      }
    },

    markStepAsSkippedForType: (state, action: PayloadAction<PromotionType>): void => {
      const step = getPromotionStepFromType(state, action.payload);
      if (step) {
        step.isSkipped = true;
        step.isCompleted = false;
      }
    },

    markStepAsCompletedForType: (state, action: PayloadAction<PromotionType>): void => {
      const step = getPromotionStepFromType(state, action.payload);
      if (step) {
        step.isCompleted = true;
        step.isSkipped = false;
      }
    },

    removeEventEmailCampaign: (state, action: PayloadAction<EmailCampaignId>): void => {
      state.emailCampaigns = state.emailCampaigns.filter((campaign) => {
        return campaign.id !== action.payload;
      });
    },

    removePrintFlyer: (state, action: PayloadAction<string>): void => {
      state.printFlyers = state.printFlyers.filter((flyer) => {
        return flyer.posterHashedID !== action.payload;
      });
    },

    setActivePromotionTypeForMobile: (state, action: PayloadAction<PromotionType>): void => {
      state.mobileViewActivePromotionType = action.payload;
    },

    setDefaultViewForMobile: (state): void => {
      state.mobileViewActivePromotionType = undefined;
    },

    setIsPreviewGeneratedForFlyerItem: (state, action: PayloadAction<{posterHashedId: string; isPreviewOutdated: boolean}>): void => {
      const flyer = state.printFlyers.find((printFlyer) => {
        return printFlyer.posterHashedID === action.payload.posterHashedId;
      });

      if (flyer) {
        flyer.isPreviewOutdated = action.payload.isPreviewOutdated;
      }
    },

    addPrintFlyer: (state, action: PayloadAction<PrintFlyerPoster>): void => {
      state.printFlyers = insert(state.printFlyers, 0, action.payload);
    },

    updatePrintFlyerPoster: (state, action: PayloadAction<PrintFlyerPoster>): void => {
      const flyer = getPrintFlyerFromHID(state, action.payload.posterHashedID);

      if (flyer) {
        Object.assign(flyer, action.payload);
      }
    },

    addEmailCampaign: (state, action: PayloadAction<EmailCampaign>): void => {
      state.emailCampaigns.unshift(action.payload);
    },

    addSocialPost: (state, action: PayloadAction<SocialPost>): void => {
      state.socialPosts.unshift(action.payload);
    },
  },
  extraReducers: mystuffEventPromotionPageExtraReducers,
});

const getPromotionStepFromType = (state: EventPromotionReducerState, promotionType: PromotionType): PromotionStep | undefined => {
  return state.promotionSteps.find((step) => {
    return step.type === promotionType;
  });
};

const getPrintFlyerFromHID = (state: EventPromotionReducerState, posterHashedId: string): PrintFlyerPoster | undefined => {
  return state.printFlyers.find((printFlyer) => {
    return printFlyer.posterHashedID === posterHashedId;
  });
};

export const {
  updatePromotionSteps,
  updatePromotionStep,
  markStepAsSkippedForType,
  markStepAsCompletedForType,
  removeEventEmailCampaign,
  removePrintFlyer,
  setActivePromotionTypeForMobile,
  setDefaultViewForMobile,
  setIsPreviewGeneratedForFlyerItem,
  updatePrintFlyerPoster,
  addPrintFlyer,
  addEmailCampaign,
  addSocialPost,
} = eventPromotionPageSlice.actions;
export const eventPromotionPageReducer = eventPromotionPageSlice.reducer;
